/* eslint-disable react/jsx-no-constructed-context-values */
import PropTypes from 'prop-types';
import FileSaver from 'file-saver';
import moment from 'moment';
import {
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';
import { http } from '../../helpers';

const DashboardContext = createContext(null);
const DATE_PICKER_HOUR = 12;
const ANALYTICS_ROUTE = '/case-api/analytics/query';
const ANALYTICS_EXPORT_ROUTE = '/case-api/analytics/export';
const DATE_RANGE_FORMAT = 'YYYY-MM-DD';
const CLINICS_ROUTE = '/case-api/clinics?size=1000&page=0&sort=name,id&archived=false';
const LABS_ROUTE = '/case-api/labs?size=1000&page=0&sort=name,id&archived=false';
const DEFAULT_EXPORT_FILENAME = 'analytics-raw-data.csv';

const getDashboardDateConfig = () => {
    const maxDate = moment()
        .startOf('day')
        .hour(DATE_PICKER_HOUR);
    const minStartDate = maxDate
        .clone()
        .subtract(3, 'years')
        .startOf('year')
        .hour(DATE_PICKER_HOUR);
    const defaultStartDate = maxDate
        .clone()
        .startOf('month')
        .hour(DATE_PICKER_HOUR);

    return {
        defaultStartDate,
        maxDate,
        minStartDate,
    };
};

const getCurrentTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Denver';

const buildAnalyticsQueries = ({
    from,
    to,
    timezone,
    selectedClinics,
    selectedLabs,
}) => {
    const filterPayload = {
        ...(selectedClinics.length ? { clinicIds: selectedClinics } : {}),
        ...(selectedLabs.length ? { labIds: selectedLabs } : {}),
    };

    return [
        {
            key: 'casesByCreatedDate',
            payload: {
                dimension: 'CREATED_DATE_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                ...filterPayload,
            },
        },
        {
            key: 'casesByClinic',
            payload: {
                dimension: 'CLINIC_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                topN: 10,
                ...filterPayload,
            },
        },
        {
            key: 'casesByLab',
            payload: {
                dimension: 'LAB_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                topN: 10,
                ...filterPayload,
            },
        },
        {
            key: 'participation',
            payload: {
                dimension: 'DISTINCT_CLINIC_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                ...filterPayload,
            },
        },
        {
            key: 'casesBySoftwareVersion',
            payload: {
                dimension: 'APP_VERSION_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                topN: 10,
                ...filterPayload,
            },
        },
        {
            key: 'casesByFirmwareVersion',
            payload: {
                dimension: 'FIRMWARE_VERSION_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                topN: 10,
                ...filterPayload,
            },
        },
        {
            key: 'casesByScanner',
            payload: {
                dimension: 'SCANNER_SERIAL_NUMBER_V0',
                measure: 'CASES_COUNT',
                dateRange: { from, to, timezone },
                topN: 10,
                ...filterPayload,
            },
        },
    ];
};

const buildAnalyticsExportPayload = ({
    selectedStartDate,
    selectedEndDate,
    selectedClinics,
    selectedLabs,
}) => ({
    dateRange: {
        from: selectedStartDate.clone().startOf('day').toISOString(),
        to: selectedEndDate.clone().add(1, 'day').startOf('day').toISOString(),
    },
    ...(selectedClinics.length ? { clinicIds: selectedClinics } : {}),
    ...(selectedLabs.length ? { labIds: selectedLabs } : {}),
});

const getExportFilename = (contentDisposition) => {
    if (!contentDisposition) {
        return DEFAULT_EXPORT_FILENAME;
    }

    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);

    return filenameMatch?.[1] || DEFAULT_EXPORT_FILENAME;
};

const hasAnalyticsRows = (analyticsResponse) => (analyticsResponse?.rows || []).length > 0;

function DashboardProvider({ children }) {
    const [dateConfig] = useState(getDashboardDateConfig);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [exportFailed, setExportFailed] = useState(false);
    const [failed, setFailed] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [timezone] = useState(getCurrentTimezone);
    const [selectedClinics, setSelectedClinics] = useState([]);
    const [selectedLabs, setSelectedLabs] = useState([]);
    const [selectedStartDate, setSelectedStartDate] = useState(dateConfig.defaultStartDate);
    const [selectedEndDate, setSelectedEndDate] = useState(dateConfig.maxDate);
    const [clinics, setClinics] = useState([]);
    const [labs, setLabs] = useState([]);
    const hasExportableAnalyticsData = hasAnalyticsRows(analyticsData?.casesByCreatedDate);

    useEffect(() => {
        let isActive = true;

        Promise.allSettled([
            http.get(CLINICS_ROUTE),
            http.get(LABS_ROUTE),
        ]).then(([clinicsResult, labsResult]) => {
            if (!isActive) return;

            setClinics(clinicsResult.status === 'fulfilled' ? clinicsResult.value.data.content : []);
            setLabs(labsResult.status === 'fulfilled' ? labsResult.value.data.content : []);
        });

        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        const queries = buildAnalyticsQueries({
            from: selectedStartDate.format(DATE_RANGE_FORMAT),
            to: selectedEndDate.clone().add(1, 'day').format(DATE_RANGE_FORMAT),
            selectedClinics,
            selectedLabs,
            timezone,
        });
        let isActive = true;

        setLoading(true);
        setExportFailed(false);
        setFailed(false);

        Promise.allSettled(
            queries.map(({ payload }) => http.post(ANALYTICS_ROUTE, payload)),
        ).then((results) => {
            if (!isActive) return;

            const hasFailure = results.some((result) => result.status === 'rejected');

            if (hasFailure) {
                setFailed(true);
                setAnalyticsData(null);
                setLoading(false);
                return;
            }

            const nextAnalyticsData = results.reduce((acc, result, index) => ({
                ...acc,
                [queries[index].key]: result.value.data,
            }), {});

            setAnalyticsData(nextAnalyticsData);
            setFailed(false);
            setLoading(false);
        }).catch(() => {
            if (!isActive) return;

            setFailed(true);
            setAnalyticsData(null);
            setLoading(false);
        });

        return () => {
            isActive = false;
        };
    }, [selectedClinics, selectedEndDate, selectedLabs, selectedStartDate, timezone]);

    const onUpdateClinics = (nextClinics) => {
        setSelectedClinics(nextClinics);
    };

    const onUpdateLabs = (nextLabs) => {
        setSelectedLabs(nextLabs);
    };

    const onUpdateDateRange = (nextStartDate, nextEndDate) => {
        setSelectedStartDate(nextStartDate);
        setSelectedEndDate(nextEndDate);
    };

    const onExportAnalytics = () => {
        if (loading || exporting || exportFailed || !hasExportableAnalyticsData) {
            return;
        }

        setExporting(true);

        const payload = buildAnalyticsExportPayload({
            selectedStartDate,
            selectedEndDate,
            selectedClinics,
            selectedLabs,
        });

        http.post(ANALYTICS_EXPORT_ROUTE, payload, {
            headers: { Accept: 'text/csv' },
            responseType: 'arraybuffer',
        }).then((response) => {
            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'text/csv',
            });
            const filename = getExportFilename(response.headers['content-disposition']);

            FileSaver.saveAs(blob, filename);
        }).catch((error) => {
            setExportFailed(true);
            console.warn('Failed to export analytics data', error);
        }).finally(() => {
            setExporting(false);
        });
    };

    const value = {
        analyticsData,
        clinics,
        defaultEndDate: dateConfig.maxDate,
        defaultStartDate: dateConfig.defaultStartDate,
        exporting,
        exportFailed,
        failed,
        hasExportableAnalyticsData,
        labs,
        loading,
        maxDate: dateConfig.maxDate,
        minStartDate: dateConfig.minStartDate,
        onExportAnalytics,
        onUpdateClinics,
        onUpdateDateRange,
        onUpdateLabs,
        selectedClinics,
        selectedEndDate,
        selectedLabs,
        selectedStartDate,
        setLoading,
        timezone,
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
}

DashboardProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

function useDashboardContext() {
    const context = useContext(DashboardContext);

    if (!context) {
        throw new Error('useDashboardContext must be used within DashboardProvider');
    }

    return context;
}

export {
    DashboardProvider,
    useDashboardContext,
};
